import React, { useEffect, useRef, useState } from "react";
import QRCodeStyling, { Options as QROptions, DotType, CornerSquareType, CornerDotType } from "qr-code-styling";
import { updateScene } from "../../utils/api";
import { generateCardDesign } from "../../utils/generateCardDesign";
import "./QREditor.css";

// ─── Types ────────────────────────────────────────────────────────────────────

type Anchor = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center";

export interface QRPlacement {
  anchor: Anchor;
  offsetX: number;  // px relative to anchor, in 400-px logical space
  offsetY: number;
  size: number;     // px in 400-px logical space
  rotation: 0 | 90 | 180 | 270;
  transparentBg: boolean;
}

export interface QRSettings {
  dotsType: DotType;
  cornersSquareType: CornerSquareType | "";
  cornersDotType: CornerDotType | "";
  bodyColor: string;
  bgColor: string;
  logo: string;
  logoSize: number;        // 0.05–0.60, default 0.25
  colorizedLogo: boolean;  // use /LogoImg.svg filled with bodyColor
  placement: QRPlacement;
}

interface QREditorProps {
  sceneId: string;
  cardImage?: string;
  initialSettings: QRSettings | null | undefined;
  onClose: () => void;
  onSaved: (savedSettings: QRSettings) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Logical coordinate space — QR offsets are stored in this space. */
const PREVIEW_W = 400;

/**
 * Fixed render size for the placement SVG.
 * qr-code-styling quantises module sizes to integers, so changing `size` by 1 px
 * only changes the visual QR every ~(size/modules) pixels.
 * Fix: render once at QR_SVG_BASE, CSS-scale to placement.size → smooth, continuous scaling.
 */
const QR_SVG_BASE = 300;

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_PLACEMENT: QRPlacement = {
  anchor: "bottom-right",
  offsetX: 0,
  offsetY: 0,
  size: 150,
  rotation: 0,
  transparentBg: false,
};

const DEFAULT_SETTINGS: QRSettings = {
  dotsType: "rounded",
  cornersSquareType: "",
  cornersDotType: "",
  bodyColor: "#92472A",
  bgColor: "#ffffff",
  logo: "https://arkaradmin.arkheops.com/LogoImg2048.png",
  logoSize: 0.25,
  colorizedLogo: false,
  placement: { ...DEFAULT_PLACEMENT },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Inject a <style> rule that fills every shape with `color`, effectively
 * colorizing a monochrome SVG logo with the QR body color.
 */
function colorizeSvg(svgText: string, color: string): string {
  const style = `<style>path,rect,circle,ellipse,polygon,polyline,line{fill:${color} !important;stroke:none !important}</style>`;
  return svgText.replace(/(<svg[^>]*>)/i, `$1${style}`);
}

function mergeSettings(saved: any | null | undefined): QRSettings {
  if (!saved) return { ...DEFAULT_SETTINGS, placement: { ...DEFAULT_PLACEMENT } };
  return {
    ...DEFAULT_SETTINGS,
    ...saved,
    placement: { ...DEFAULT_PLACEMENT, ...(saved.placement ?? {}) },
  };
}

function buildQROptions(settings: QRSettings, logoUrl: string, size = 250): QROptions {
  return {
    width: size,
    height: size,
    data: "https://arkheops.com",
    ...(logoUrl ? { image: logoUrl } : {}),
    dotsOptions: { type: settings.dotsType, color: settings.bodyColor },
    cornersSquareOptions: {
      ...(settings.cornersSquareType ? { type: settings.cornersSquareType as CornerSquareType } : {}),
      color: settings.bodyColor,
    },
    cornersDotOptions: {
      ...(settings.cornersDotType ? { type: settings.cornersDotType as CornerDotType } : {}),
      color: settings.bodyColor,
    },
    backgroundOptions: { color: settings.placement.transparentBg ? "transparent" : settings.bgColor },
    imageOptions: { hideBackgroundDots: true, imageSize: settings.logoSize, margin: 6 },
    qrOptions: { errorCorrectionLevel: "H" },
  };
}

/**
 * SVG options for the placement overlay.
 * Always rendered at QR_SVG_BASE — no logo (avoids CORS), purely vector.
 * The container div is then CSS-scaled to placement.size for smooth sizing.
 */
function buildQROptionsPlacement(settings: QRSettings): QROptions {
  return {
    width: QR_SVG_BASE,
    height: QR_SVG_BASE,
    data: "https://arkheops.com",
    dotsOptions: { type: settings.dotsType, color: settings.bodyColor },
    cornersSquareOptions: {
      ...(settings.cornersSquareType ? { type: settings.cornersSquareType as CornerSquareType } : {}),
      color: settings.bodyColor,
    },
    cornersDotOptions: {
      ...(settings.cornersDotType ? { type: settings.cornersDotType as CornerDotType } : {}),
      color: settings.bodyColor,
    },
    backgroundOptions: { color: settings.placement.transparentBg ? "transparent" : settings.bgColor },
    qrOptions: { errorCorrectionLevel: "H" },
  };
}

/**
 * Returns the desired visual top-left of the QR box (in 400-px logical space).
 */
function computeQRTopLeft(p: QRPlacement, logicalH: number): { left: number; top: number } {
  const { anchor, offsetX, offsetY, size } = p;
  switch (anchor) {
    case "top-left":     return { left: offsetX,                    top: offsetY };
    case "top-right":    return { left: PREVIEW_W - offsetX - size, top: offsetY };
    case "bottom-left":  return { left: offsetX,                    top: logicalH - offsetY - size };
    case "bottom-right": return { left: PREVIEW_W - offsetX - size, top: logicalH - offsetY - size };
    case "center":       return { left: PREVIEW_W / 2 - size / 2 + offsetX, top: logicalH / 2 - size / 2 + offsetY };
  }
}

/**
 * Maps a drag delta (logical-space px) back to new offsetX/offsetY for the current anchor.
 * Positive dx = QR moves right; positive dy = QR moves down.
 */
function applyDragDelta(
  anchor: Anchor,
  startOffX: number, startOffY: number,
  dx: number, dy: number,
): { x: number; y: number } {
  switch (anchor) {
    case "top-left":     return { x: startOffX + dx, y: startOffY + dy };
    case "top-right":    return { x: startOffX - dx, y: startOffY + dy };
    case "bottom-left":  return { x: startOffX + dx, y: startOffY - dy };
    case "bottom-right": return { x: startOffX - dx, y: startOffY - dy };
    case "center":       return { x: startOffX + dx, y: startOffY + dy };
  }
}

// ─── Option lists ─────────────────────────────────────────────────────────────

const DOTS_TYPES: DotType[] = ["square", "dots", "rounded", "classy", "classy-rounded", "extra-rounded"];
const CORNERS_SQUARE_TYPES: Array<CornerSquareType | ""> = [
  "", "square", "dot", "extra-rounded", "rounded", "dots", "classy", "classy-rounded",
];
const CORNERS_DOT_TYPES: Array<CornerDotType | ""> = [
  "", "square", "dot", "extra-rounded", "rounded", "dots", "classy", "classy-rounded",
];
const DISPLAY_LABEL: Record<string, string> = { "": "none (follow dots style)" };

const ANCHORS: { value: Anchor; label: string }[] = [
  { value: "top-left",     label: "↖" },
  { value: "top-right",    label: "↗" },
  { value: "center",       label: "⊙" },
  { value: "bottom-left",  label: "↙" },
  { value: "bottom-right", label: "↘" },
];

const ROTATIONS: Array<0 | 90 | 180 | 270> = [0, 90, 180, 270];

// ─── QR canvas / SVG hook ────────────────────────────────────────────────────

function useQRCanvas(
  containerRef: React.RefObject<HTMLDivElement | null>,
  options: QROptions,
  forceType?: "canvas" | "svg",
  triggerKey?: unknown,
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";
    const qr = new QRCodeStyling(forceType ? { ...options, type: forceType as any } : options);
    qr.append(container);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(options), forceType, triggerKey]);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function QREditor({ sceneId, cardImage, initialSettings, onClose, onSaved }: QREditorProps) {
  const [settings, setSettings] = useState<QRSettings>(() => mergeSettings(initialSettings));
  const [tab, setTab] = useState<"style" | "placement">("style");
  const [isSaving, setIsSaving] = useState(false);
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState<string>(() => mergeSettings(initialSettings).logo);

  // Resolve logo URL: either the user-supplied URL, or a colorized /LogoImg.svg data URL
  useEffect(() => {
    if (!settings.colorizedLogo) {
      setResolvedLogoUrl(settings.logo);
      return;
    }
    let cancelled = false;
    fetch("/LogoImg.svg")
      .then((r) => r.text())
      .then((svgText) => {
        if (cancelled) return;
        const colored = colorizeSvg(svgText, settings.bodyColor);
        const dataUrl = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(colored)));
        setResolvedLogoUrl(dataUrl);
      })
      .catch(() => { if (!cancelled) setResolvedLogoUrl(settings.logo); });
    return () => { cancelled = true; };
  }, [settings.colorizedLogo, settings.logo, settings.bodyColor]);
  const [isPreviewDownloading, setIsPreviewDownloading] = useState(false);
  const [imgNaturalSize, setImgNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [zoomPct, setZoomPct] = useState(100);
  const [containerW, setContainerW] = useState(400);
  const fitZoomedRef = useRef(false);

  // Logical canvas height (aspect-correct, in 400-px space)
  const logicalH = imgNaturalSize
    ? Math.round(PREVIEW_W * imgNaturalSize.height / imgNaturalSize.width)
    : PREVIEW_W;

  // 100 % zoom = 1 image pixel : 1 screen pixel
  const scale100    = imgNaturalSize ? imgNaturalSize.width / PREVIEW_W : 1;
  const totalScale  = scale100 * (zoomPct / 100);
  const fitZoomPct  = imgNaturalSize
    ? Math.max(1, Math.round((containerW / imgNaturalSize.width) * 100))
    : 100;

  // Viewport size = column width × aspect-correct height at fit zoom (no scroll at fit)
  const viewportW = containerW;
  const viewportH = Math.round(logicalH * (containerW / PREVIEW_W));
  // Scaled content size (may exceed viewport when zoomed in → scrollable)
  const scaledW = Math.round(PREVIEW_W * totalScale);
  const scaledH = Math.round(logicalH  * totalScale);

  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Style tab — canvas (supports logo)
  const stylePreviewRef = useRef<HTMLDivElement>(null);
  useQRCanvas(stylePreviewRef, buildQROptions(settings, resolvedLogoUrl, 250));

  // Placement tab — SVG at QR_SVG_BASE, CSS-scaled to placement.size
  // triggerKey=tab ensures the effect re-runs after the div mounts on tab switch
  const placementQRRef = useRef<HTMLDivElement>(null);
  useQRCanvas(placementQRRef, buildQROptionsPlacement(settings), "svg", tab);

  // Measure available column width
  useEffect(() => {
    if (tab !== "placement") return;
    const el = previewContainerRef.current;
    if (!el) return;
    const measure = () => setContainerW(Math.max(1, el.clientWidth - 40));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tab]);

  // Auto fit-zoom once on first image load
  useEffect(() => {
    if (fitZoomedRef.current || !imgNaturalSize || containerW <= 1) return;
    fitZoomedRef.current = true;
    setZoomPct(fitZoomPct);
  }, [imgNaturalSize, containerW, fitZoomPct]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // ── Drag ──────────────────────────────────────────────────────────────────

  const dragRef = useRef<{
    startScreenX: number;
    startScreenY: number;
    startOffX: number;
    startOffY: number;
    anchor: Anchor;
    scale: number;
  } | null>(null);

  const handleQRMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startScreenX: e.clientX,
      startScreenY: e.clientY,
      startOffX: settings.placement.offsetX,
      startOffY: settings.placement.offsetY,
      anchor: settings.placement.anchor,
      scale: totalScale,
    };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const { startScreenX, startScreenY, startOffX, startOffY, anchor, scale } = dragRef.current;
      const dx = (ev.clientX - startScreenX) / scale;
      const dy = (ev.clientY - startScreenY) / scale;
      const { x, y } = applyDragDelta(anchor, startOffX, startOffY, dx, dy);
      setSettings((prev) => ({
        ...prev,
        placement: { ...prev.placement, offsetX: Math.round(x), offsetY: Math.round(y) },
      }));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const handlePreviewDownload = async () => {
    if (!cardImage) return;
    setIsPreviewDownloading(true);
    try {
      await generateCardDesign(
        "preview",
        cardImage,
        settings,
        "preview",
        "https://arkheops.com",
      );
    } catch (err) {
      console.error("Error generating preview:", err);
      alert("Failed to generate preview image.");
    } finally {
      setIsPreviewDownloading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateScene(sceneId, { qr_settings: settings } as any);
      onSaved(settings);
      onClose();
    } catch (err) {
      console.error("Error saving QR settings:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const set = <K extends keyof QRSettings>(key: K, value: QRSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const setPlacement = <K extends keyof QRPlacement>(key: K, value: QRPlacement[K]) =>
    setSettings((prev) => ({ ...prev, placement: { ...prev.placement, [key]: value } }));

  const { placement } = settings;

  // ── QR overlay position ───────────────────────────────────────────────────
  // The inner ref div renders at QR_SVG_BASE × QR_SVG_BASE.
  // We CSS-scale it from its center to placement.size × placement.size.
  // Compensate the position so the visual top-left lands at (desiredLeft, desiredTop).
  const { left: desiredLeft, top: desiredTop } = computeQRTopLeft(placement, logicalH);
  const centerOffset = (QR_SVG_BASE - placement.size) / 2;
  const overlayLeft  = desiredLeft - centerOffset;
  const overlayTop   = desiredTop  - centerOffset;

  return (
    <div className="qr-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="qr-modal qr-modal--wide" role="dialog" aria-modal="true" aria-label="QR Style Editor">

        {/* ── Header ── */}
        <div className="qr-modal-header">
          <h2 className="qr-modal-title">Edit QR Style</h2>
          <div className="qr-tabs">
            <button className={`qr-tab-btn${tab === "style" ? " qr-tab-btn--active" : ""}`} onClick={() => setTab("style")}>Style</button>
            <button className={`qr-tab-btn${tab === "placement" ? " qr-tab-btn--active" : ""}`} onClick={() => setTab("placement")}>Placement</button>
          </div>
          <button className="qr-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ══ STYLE TAB ═══════════════════════════════════════════════════════ */}
        {tab === "style" && (
          <div className="qr-modal-body">
            <div className="qr-controls">

              <div className="qr-field-group">
                <h3 className="qr-section-label">Dot Style</h3>
                <div className="qr-field">
                  <label htmlFor="qr-dots-type">Body dots</label>
                  <select id="qr-dots-type" value={settings.dotsType}
                    onChange={(e) => set("dotsType", e.target.value as DotType)}>
                    {DOTS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="qr-field">
                  <label htmlFor="qr-corners-square-type">Eye frame</label>
                  <select id="qr-corners-square-type" value={settings.cornersSquareType}
                    onChange={(e) => set("cornersSquareType", e.target.value as CornerSquareType | "")}>
                    {CORNERS_SQUARE_TYPES.map((t) => <option key={t} value={t}>{DISPLAY_LABEL[t] ?? t}</option>)}
                  </select>
                </div>
                <div className="qr-field">
                  <label htmlFor="qr-corner-dot-type">Eye ball</label>
                  <select id="qr-corner-dot-type" value={settings.cornersDotType}
                    onChange={(e) => set("cornersDotType", e.target.value as CornerDotType | "")}>
                    {CORNERS_DOT_TYPES.map((t) => <option key={t} value={t}>{DISPLAY_LABEL[t] ?? t}</option>)}
                  </select>
                </div>
              </div>

              <div className="qr-field-group">
                <h3 className="qr-section-label">Colors</h3>
                <div className="qr-field qr-field--color">
                  <label htmlFor="qr-body-color">Body &amp; eyes color</label>
                  <div className="qr-color-input">
                    <input type="color" id="qr-body-color" value={settings.bodyColor}
                      onChange={(e) => set("bodyColor", e.target.value)} />
                    <span className="qr-color-value">{settings.bodyColor.toUpperCase()}</span>
                  </div>
                </div>
                <div className="qr-field qr-field--color">
                  <label htmlFor="qr-bg-color">Background color</label>
                  <div className="qr-color-input">
                    <input type="color" id="qr-bg-color" value={settings.bgColor}
                      onChange={(e) => set("bgColor", e.target.value)}
                      disabled={placement.transparentBg} />
                    <span className="qr-color-value">{placement.transparentBg ? "—" : settings.bgColor.toUpperCase()}</span>
                    <label className="qr-checkbox-label">
                      <input type="checkbox" checked={placement.transparentBg}
                        onChange={(e) => setPlacement("transparentBg", e.target.checked)} />
                      Transparent
                    </label>
                  </div>
                </div>
              </div>

              <div className="qr-field-group">
                <h3 className="qr-section-label">Logo</h3>
                <div className="qr-field">
                  <label className="qr-checkbox-label">
                    <input type="checkbox" checked={settings.colorizedLogo}
                      onChange={(e) => set("colorizedLogo", e.target.checked)} />
                    Colorized logo (LogoImg.svg with QR color)
                  </label>
                </div>
                <div className="qr-field">
                  <label htmlFor="qr-logo">Logo URL</label>
                  <input type="text" id="qr-logo" value={settings.logo}
                    placeholder="https://…"
                    disabled={settings.colorizedLogo}
                    onChange={(e) => set("logo", e.target.value)} />
                </div>
                <div className="qr-field">
                  <label htmlFor="qr-logo-size">Logo size — {Math.round(settings.logoSize * 100)}%</label>
                  <div className="qr-size-row">
                    <input type="range" id="qr-logo-size" min={5} max={60} step={1}
                      value={Math.round(settings.logoSize * 100)}
                      onChange={(e) => set("logoSize", Number(e.target.value) / 100)} />
                    <span className="qr-size-value">{Math.round(settings.logoSize * 100)}%</span>
                  </div>
                </div>
              </div>

            </div>

            <div className="qr-preview-panel">
              <p className="qr-preview-label">Preview</p>
              <div className="qr-preview-canvas" ref={stylePreviewRef} />
              <p className="qr-preview-hint">Links to <strong>https://arkheops.com</strong></p>
            </div>
          </div>
        )}

        {/* ══ PLACEMENT TAB ═══════════════════════════════════════════════════ */}
        {tab === "placement" && (
          <div className="qr-modal-body qr-modal-body--placement">

            {/* Left: controls */}
            <div className="qr-controls">

              <div className="qr-field-group">
                <h3 className="qr-section-label">Anchor</h3>
                <div className="qr-anchor-grid">
                  {ANCHORS.map(({ value, label }) => (
                    <button key={value}
                      className={`qr-anchor-btn qr-anchor-btn--${value}${placement.anchor === value ? " qr-anchor-btn--active" : ""}`}
                      onClick={() => setPlacement("anchor", value)}
                      title={value}
                    >{label}</button>
                  ))}
                </div>
              </div>

              <div className="qr-field-group">
                <h3 className="qr-section-label">Offset (px)</h3>
                <div className="qr-field-row">
                  <div className="qr-field">
                    <label htmlFor="qr-offset-x">X</label>
                    <input type="number" id="qr-offset-x" value={placement.offsetX}
                      onChange={(e) => setPlacement("offsetX", Number(e.target.value))} />
                  </div>
                  <div className="qr-field">
                    <label htmlFor="qr-offset-y">Y</label>
                    <input type="number" id="qr-offset-y" value={placement.offsetY}
                      onChange={(e) => setPlacement("offsetY", Number(e.target.value))} />
                  </div>
                </div>
              </div>

              <div className="qr-field-group">
                <h3 className="qr-section-label">Size (px)</h3>
                <div className="qr-field">
                  <div className="qr-size-row">
                    <input type="range" min={20} max={400} step={1} value={placement.size}
                      onChange={(e) => setPlacement("size", Number(e.target.value))} />
                    <input type="number" min={20} max={400} value={placement.size}
                      onChange={(e) => setPlacement("size", Math.max(20, Math.min(400, Number(e.target.value))))} />
                  </div>
                </div>
              </div>

              <div className="qr-field-group">
                <h3 className="qr-section-label">Rotation</h3>
                <div className="qr-rotation-group">
                  {ROTATIONS.map((r) => (
                    <button key={r}
                      className={`qr-rotation-btn${placement.rotation === r ? " qr-rotation-btn--active" : ""}`}
                      onClick={() => setPlacement("rotation", r)}
                    >{r}°</button>
                  ))}
                </div>
              </div>

            </div>

            {/* Right: card image + QR overlay */}
            <div className="qr-placement-preview" ref={previewContainerRef}>
              <div className="qr-preview-header">
                <p className="qr-preview-label">Placement Preview</p>
                <div className="qr-zoom-controls">
                  <button className="qr-zoom-btn" onClick={() => setZoomPct((p) => Math.max(1, p - 25))}>−</button>
                  <span className="qr-zoom-value">{zoomPct}%</span>
                  <button className="qr-zoom-btn" onClick={() => setZoomPct((p) => Math.min(400, p + 25))}>+</button>
                  <button className="qr-zoom-btn qr-zoom-btn--reset" onClick={() => setZoomPct(fitZoomPct)}>Fit</button>
                  <button className="qr-zoom-btn qr-zoom-btn--reset" onClick={() => setZoomPct(100)}>100%</button>
                </div>
              </div>

              {/* Scroll viewport: fixed to column width × aspect-correct height at fit zoom */}
              <div
                className="qr-card-canvas"
                style={{ width: viewportW, height: viewportH, overflow: "auto" }}
              >
                {/* Spacer: tells browser how large the scrollable area is */}
                <div style={{ width: scaledW, height: scaledH, position: "relative", flexShrink: 0 }}>
                  {/* Logical 400-px world, CSS-scaled uniformly */}
                  <div style={{
                    width: PREVIEW_W,
                    height: logicalH,
                    transform: `scale(${totalScale})`,
                    transformOrigin: "top left",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    overflow: "hidden",
                  }}>
                    {cardImage
                      ? <img
                          src={cardImage}
                          alt="Card"
                          className="qr-card-img"
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            setImgNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                          }}
                        />
                      : <div className="qr-card-placeholder">No card image uploaded</div>
                    }
                    {/*
                      QR overlay: positioned at (overlayLeft, overlayTop) in logical space.
                      The inner div renders at QR_SVG_BASE × QR_SVG_BASE (SVG).
                      CSS scale(size/BASE) around center maps it visually to placement.size.
                      Rotation is applied after scale, also around center.
                    */}
                    <div
                      style={{
                        position: "absolute",
                        left: overlayLeft,
                        top: overlayTop,
                        width: QR_SVG_BASE,
                        height: QR_SVG_BASE,
                        transform: `scale(${placement.size / QR_SVG_BASE}) rotate(${placement.rotation}deg)`,
                        transformOrigin: "center center",
                        cursor: "grab",
                        userSelect: "none",
                        lineHeight: 0,
                      }}
                      onMouseDown={handleQRMouseDown}
                    >
                      <div ref={placementQRRef} />
                    </div>
                  </div>
                </div>
              </div>

              <p className="qr-preview-hint">
                Drag QR to reposition · Fit = fill column · 100% = native image resolution
              </p>
            </div>

          </div>
        )}

        {/* ── Footer ── */}
        <div className="qr-modal-footer">
          <div className="qr-footer-left">
            {tab === "placement" && cardImage && (
              <button
                className="btn-ark qr-preview-download-btn"
                onClick={handlePreviewDownload}
                disabled={isPreviewDownloading}
                title="Download PNG with QR at https://arkheops.com to test placement"
              >
                {isPreviewDownloading ? "Generating…" : "⬇ Download preview PNG"}
              </button>
            )}
          </div>
          <div className="qr-footer-right">
            <button className="btn-secondary" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button className="btn-ark" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
